// Ported from the concept site's src/components. Every effect is set up per section so the theme editor can reload one section at a time.
;(() => {
  const STILL = matchMedia('(prefers-reduced-motion: reduce)').matches
  const PHONE = matchMedia('(pointer: coarse)').matches
  const css = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const tints = () => [css('--accent-1'), css('--accent-2'), css('--accent-3')]

  const layer = (w, h) => {
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    return c
  }

  // Art that assembles from grains when it scrolls into view and scatters around the pointer. Returns a cleanup.
  // opts: srcs, bleed (canvas overhang per side), radius (pointer push as a fraction of width), cell, paint, halo,
  // onLive (when set, the canvas only draws while grains are loose and the real image shows the rest of the time).
  function startGrain(canvas, host, opts) {
    const ctx = canvas.getContext('2d')
    let images = []
    let grains = []
    let art = layer(1, 1)
    let halo = layer(1, 1)
    let w = 0
    let cell = 5
    let dpr = 1
    let raf = 0
    let start = 0
    let lastFrame = 0
    let assembling = !STILL
    let visible = false
    let loading = false
    let alive = true
    const pointer = { x: 0, y: 0, on: false }

    const paint = (c, scaleX, scaleY, glowing) => {
      const inner = 1 + opts.bleed * 2
      const h = canvas.offsetHeight / inner
      c.scale(scaleX, scaleY)
      c.translate(w * opts.bleed, h * opts.bleed)
      opts.paint(c, w, h, images, glowing)
    }

    const build = () => {
      if (!images.length) return
      const box = canvas.getBoundingClientRect()
      if (!box.width || !box.height) return
      const inner = 1 + opts.bleed * 2
      w = canvas.offsetWidth / inner
      const fullW = canvas.offsetWidth
      const fullH = canvas.offsetHeight
      // On phones, art that hands off to a real image only needs the canvas while it moves.
      dpr = PHONE && opts.onLive ? 1 : Math.min(devicePixelRatio, 2)
      canvas.width = fullW * dpr
      canvas.height = fullH * dpr
      cell = Math.round(opts.cell(w, fullH / inner) * (PHONE ? 1.5 : 1))
      art = layer(canvas.width, canvas.height)
      paint(art.getContext('2d'), dpr, dpr, false)
      if (opts.halo) {
        halo = layer(canvas.width, canvas.height)
        paint(halo.getContext('2d'), dpr, dpr, true)
      }

      // One grain per cell, colored from the art underneath it.
      const cols = Math.ceil(fullW / cell)
      const rows = Math.ceil(fullH / cell)
      const sample = layer(cols, rows)
      const s = sample.getContext('2d', { willReadFrequently: true })
      // Painted fresh rather than copied from art, since reading art back stalls the GPU mid scroll.
      paint(s, cols / fullW, rows / fullH, false)
      const px = s.getImageData(0, 0, cols, rows).data
      grains = []
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = (y * cols + x) * 4
          if (px[i + 3] < 200) continue
          const hx = x * cell
          const hy = y * cell
          const a = Math.random() * 6.28
          const d = assembling ? w * (0.04 + Math.random() ** 2 * 0.3) : 0
          grains.push({ hx, hy, x: hx + Math.cos(a) * d, y: hy + Math.sin(a) * d, vx: 0, vy: 0, c: `rgb(${px[i]},${px[i + 1]},${px[i + 2]})`, delay: Math.random() * 500 })
        }
      }
      render()
      wake()
    }

    const render = () => {
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const loose = grains.filter((g) => Math.abs(g.x - g.hx) + Math.abs(g.y - g.hy) > 0.6)
      if (opts.onLive) opts.onLive(loose.length > 0)
      if (opts.onLive && !loose.length) return
      ctx.drawImage(art, 0, 0)

      // A grain that has left home punches a hole in the art and draws itself where it is.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      for (const g of loose) ctx.clearRect(g.hx, g.hy, cell, cell)
      if (opts.halo) {
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.globalCompositeOperation = 'destination-over'
        ctx.drawImage(halo, 0, 0)
        ctx.globalCompositeOperation = 'source-over'
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      }
      for (const g of loose) {
        ctx.fillStyle = g.c
        ctx.fillRect(g.x, g.y, cell - 1, cell - 1)
      }
    }

    const step = (t) => {
      // The image may have no layout yet. The build that follows wakes this back up.
      if (!grains.length) {
        raf = 0
        return
      }
      if (!start) start = t
      const elapsed = t - start
      const radius = w * opts.radius
      // A slow device takes more physics per frame, so the assembly lasts the same time everywhere.
      const steps = lastFrame ? Math.min(4, Math.max(1, Math.round((t - lastFrame) / 16.7))) : 1
      lastFrame = t
      let moving = false
      for (const g of grains) {
        if (assembling && elapsed < g.delay) {
          moving = true
          continue
        }
        for (let i = 0; i < steps; i++) {
          if (pointer.on) {
            const dx = g.x - pointer.x
            const dy = g.y - pointer.y
            const d2 = dx * dx + dy * dy
            if (d2 < radius * radius && d2 > 0.01) {
              const d = Math.sqrt(d2)
              const f = (1 - d / radius) * 2.4
              g.vx += (dx / d) * f - (dy / d) * f * 0.35
              g.vy += (dy / d) * f + (dx / d) * f * 0.35
            }
          }
          g.vx = (g.vx + (g.hx - g.x) * 0.045) * 0.84
          g.vy = (g.vy + (g.hy - g.y) * 0.045) * 0.84
          g.x += g.vx
          g.y += g.vy
        }
        if (Math.abs(g.vx) + Math.abs(g.vy) < 0.05 && Math.abs(g.x - g.hx) + Math.abs(g.y - g.hy) < 0.6) {
          g.x = g.hx
          g.y = g.hy
          g.vx = 0
          g.vy = 0
        } else {
          moving = true
        }
      }
      render()
      if (!moving) assembling = false
      // Sleeps once every grain is home or the art is offscreen, so an idle page costs nothing.
      raf = visible && (moving || pointer.on) ? requestAnimationFrame(step) : 0
      if (!raf) lastFrame = 0
    }

    const wake = () => {
      if (!STILL && !raf && visible) raf = requestAnimationFrame(step)
    }
    const move = (e) => {
      const r = canvas.getBoundingClientRect()
      pointer.x = e.clientX - r.left
      pointer.y = e.clientY - r.top
      pointer.on = true
      wake()
    }
    const leave = () => {
      pointer.on = false
    }

    const resize = new ResizeObserver(build)

    // Nothing loads or builds until the art first comes into view. Shopify's CDN sends CORS headers, so the canvas stays readable.
    const load = () => {
      loading = true
      Promise.all(opts.srcs.map((src) => new Promise((done) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.src = src
        img.decode().then(() => done(img))
      }))).then((loaded) => {
        if (!alive) return
        images = loaded
        build()
        resize.observe(canvas)
      })
    }

    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible && !loading) load()
      if (visible) wake()
    }, { threshold: 0.12 })
    seen.observe(host)

    host.addEventListener('pointermove', move)
    host.addEventListener('pointerdown', move)
    host.addEventListener('pointerleave', leave)
    host.addEventListener('pointerup', leave)
    host.addEventListener('pointercancel', leave)
    return () => {
      alive = false
      cancelAnimationFrame(raf)
      resize.disconnect()
      seen.disconnect()
      host.removeEventListener('pointermove', move)
      host.removeEventListener('pointerdown', move)
      host.removeEventListener('pointerleave', leave)
      host.removeEventListener('pointerup', leave)
      host.removeEventListener('pointercancel', leave)
    }
  }

  // A cover image that dissolves into grains. Markup comes from snippets/grain-image.liquid.
  function grainImage(host) {
    const img = host.querySelector('img')
    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-hidden', 'true')
    host.append(canvas)
    host.classList.add('grain-live')
    const stop = startGrain(canvas, host, {
      srcs: [img.getAttribute('src')],
      bleed: 0.14,
      radius: 0.38,
      cell: (w, h) => Math.max(4, Math.round(Math.sqrt((w * h) / 3500))),
      paint: (c, w, h, images) => c.drawImage(images[0], 0, 0, w, h),
      onLive: (live) => host.classList.toggle('grain-live', live),
    })
    return () => {
      stop()
      canvas.remove()
    }
  }

  // The three fanned cards in the hero. Back cards first. tx, ty are fractions of the card size, rot is degrees.
  const FAN = [
    { tx: -0.42, ty: 0.05, rot: -13 },
    { tx: 0.42, ty: 0.05, rot: 12 },
    { tx: 0, ty: 0, rot: 0 },
  ]
  function grainFan(canvas) {
    const cards = JSON.parse(canvas.dataset.cards).slice(0, FAN.length)
    const place = FAN.slice(FAN.length - cards.length)
    return startGrain(canvas, canvas.parentElement, {
      srcs: cards.map((card) => card.src),
      bleed: 0.14,
      radius: 0.15,
      halo: true,
      cell: (w) => Math.max(4, Math.round(w / 120)),
      paint: (c, w, h, images, glowing) => {
        const size = w * 0.58
        cards.forEach((card, i) => {
          const img = images[i]
          const ch = (size * img.naturalHeight) / img.naturalWidth
          c.save()
          c.translate(w * 0.26 + size / 2 + place[i].tx * size, h * 0.03 + ch / 2)
          c.rotate((place[i].rot * Math.PI) / 180)
          c.translate(0, place[i].ty * ch)
          if (glowing) {
            c.shadowColor = card.glow
            c.shadowBlur = 40 * Math.min(devicePixelRatio, 2)
          }
          c.drawImage(img, -size / 2, -ch / 2, size, ch)
          c.restore()
        })
      },
    })
  }

  // His words as a band of dots: it runs on its own, surges with the scroll and parts around the pointer.
  const ROWS = 18
  function marquee(host) {
    const text = host.dataset.text
    const canvas = host.querySelector('canvas')
    const ctx = canvas.getContext('2d')
    const TINT = tints()
    let lit = new Uint8Array(0)
    let cols = 1
    let pitch = 6
    let w = 0
    let h = 0
    let raf = 0
    let last = 0
    let offset = 0
    let lastScroll = scrollY
    let surge = 0
    let visible = false
    const pointer = { x: 0, y: 0, on: false }

    const raster = () => {
      const font = css('--display')
      const measure = document.createElement('canvas').getContext('2d', { willReadFrequently: true })
      measure.font = `italic ${ROWS - 2}px ${font}`
      cols = Math.max(1, Math.ceil(measure.measureText(text).width))
      measure.canvas.width = cols
      measure.canvas.height = ROWS
      measure.font = `italic ${ROWS - 2}px ${font}`
      measure.textBaseline = 'middle'
      measure.fillStyle = '#fff'
      measure.fillText(text, 0, ROWS / 2)
      const px = measure.getImageData(0, 0, cols, ROWS).data
      lit = new Uint8Array(cols * ROWS)
      for (let i = 0; i < lit.length; i++) lit[i] = px[i * 4 + 3] > 110 ? 1 : 0
    }

    const size = () => {
      const dpr = Math.min(devicePixelRatio, 2)
      w = canvas.offsetWidth
      h = canvas.offsetHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      pitch = h / ROWS
      draw(last)
    }

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h)
      const radius = h * 0.8
      const first = Math.floor(offset / pitch)
      const dot = pitch * 0.74
      for (let i = 0; i <= w / pitch + 1; i++) {
        const col = (first + i) % cols
        const x = i * pitch - (offset % pitch)
        const shine = 0.55 + 0.45 * Math.sin((first + i) * 0.09 - t * 0.004)
        for (let row = 0; row < ROWS; row++) {
          if (!lit[row * cols + col]) continue
          let dx = x
          let dy = row * pitch
          let color = '#fff'
          if (pointer.on) {
            const ax = dx - pointer.x
            const ay = dy - pointer.y
            const d = Math.hypot(ax, ay)
            if (d < radius && d > 0.01) {
              const f = (1 - d / radius) ** 2 * radius * 0.7
              dx += (ax / d) * f
              dy += (ay / d) * f
              color = TINT[(col + row) % TINT.length]
            }
          }
          ctx.globalAlpha = color === '#fff' ? shine : 1
          ctx.fillStyle = color
          ctx.fillRect(dx, dy, dot, dot)
        }
      }
      ctx.globalAlpha = 1
    }

    const step = (t) => {
      const dt = Math.min(t - last, 50)
      last = t
      surge += (scrollY - lastScroll - surge) * 0.12
      lastScroll = scrollY
      const speed = h * 0.5 * (dt / 1000) + surge * 0.6
      offset = (((offset + speed) % (cols * pitch)) + cols * pitch) % (cols * pitch)
      draw(t)
      raf = requestAnimationFrame(step)
    }

    const move = (e) => {
      const r = canvas.getBoundingClientRect()
      pointer.x = e.clientX - r.left
      pointer.y = e.clientY - r.top
      pointer.on = true
    }
    const leave = () => {
      pointer.on = false
    }

    let alive = true
    const resize = new ResizeObserver(size)
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      cancelAnimationFrame(raf)
      if (visible && !STILL) {
        last = performance.now()
        lastScroll = scrollY
        raf = requestAnimationFrame(step)
      }
    })
    document.fonts.load(`italic 16px ${css('--display').split(',')[0]}`).then(() => {
      if (!alive) return
      raster()
      size()
      resize.observe(canvas)
      seen.observe(host)
    })
    host.addEventListener('pointermove', move)
    host.addEventListener('pointerleave', leave)
    return () => {
      alive = false
      cancelAnimationFrame(raf)
      resize.disconnect()
      seen.disconnect()
      host.removeEventListener('pointermove', move)
      host.removeEventListener('pointerleave', leave)
    }
  }

  // The home menu: hovering a row swaps the card beside it.
  function indexMenu(section) {
    const card = section.querySelector('.index-card')
    if (!card) return () => {}
    const host = card.querySelector('.grain-img')
    const img = host.querySelector('img')
    const rows = [...section.querySelectorAll('.index-row')]
    let stop = STILL ? () => {} : grainImage(host)
    let current = 0
    const show = (i) => {
      if (i === current) return
      current = i
      rows.forEach((row, j) => row.classList.toggle('on', j === i))
      card.style.setProperty('--glow', rows[i].dataset.glow)
      img.src = rows[i].dataset.img
      if (!STILL) {
        stop()
        stop = grainImage(host)
      }
    }
    const handlers = rows.map((row, i) => {
      const on = () => show(i)
      row.addEventListener('mouseenter', on)
      row.addEventListener('focus', on)
      return on
    })
    const reset = () => show(0)
    section.addEventListener('mouseleave', reset)
    return () => {
      stop()
      rows.forEach((row, i) => {
        row.removeEventListener('mouseenter', handlers[i])
        row.removeEventListener('focus', handlers[i])
      })
      section.removeEventListener('mouseleave', reset)
    }
  }

  function tabs(root) {
    const buttons = [...root.querySelectorAll('[role=tab]')]
    const panels = [...root.querySelectorAll('[role=tabpanel]')]
    const pick = (e) => {
      const b = e.currentTarget
      buttons.forEach((x) => {
        x.classList.toggle('on', x === b)
        x.setAttribute('aria-selected', x === b)
      })
      panels.forEach((p) => { p.hidden = p.id !== b.getAttribute('aria-controls') })
    }
    buttons.forEach((b) => b.addEventListener('click', pick))
    return () => buttons.forEach((b) => b.removeEventListener('click', pick))
  }

  // Any text that appears, on load or in a new tab, dissolves in when it scrolls into view.
  const TARGETS = 'main h1, main h2, main h3, main p, main li, .index-row, .product-meta, .feed-tabs'
  const FRAMES = 9
  const TILE = 128
  const CELL = 4
  // Pre-renders one tile of noise per dissolve stage, so text resolves out of grain at no per-frame cost.
  function makeFrames() {
    const n = TILE / CELL
    const order = Array.from({ length: n * n }, () => Math.random())
    const c = document.createElement('canvas')
    c.width = c.height = TILE
    const ctx = c.getContext('2d')
    ctx.fillStyle = '#000'
    for (let f = 0; f < FRAMES; f++) {
      ctx.clearRect(0, 0, TILE, TILE)
      order.forEach((at, i) => {
        if (at < f / (FRAMES - 1)) ctx.fillRect((i % n) * CELL, Math.floor(i / n) * CELL, CELL, CELL)
      })
      document.documentElement.style.setProperty(`--d${f}`, `url(${c.toDataURL()})`)
    }
  }
  const seen = STILL ? null : new IntersectionObserver((entries) => {
    let i = 0
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      const el = entry.target
      seen.unobserve(el)
      el.style.animationDelay = `${Math.min(i++, 10) * 45}ms`
      el.classList.add('dither-in')
      el.addEventListener('animationend', () => {
        el.classList.remove('dither-wait', 'dither-in')
        el.style.animationDelay = ''
      }, { once: true })
    }
  }, { rootMargin: '0px 0px -6% 0px' })
  function reveal(root) {
    if (STILL) return
    const watch = (el) => {
      if (el.classList.contains('dither-wait')) return
      el.classList.add('dither-wait')
      seen.observe(el)
    }
    if (root.matches(TARGETS)) watch(root)
    root.querySelectorAll(TARGETS).forEach(watch)
  }

  function stars() {
    const canvas = document.querySelector('.stars')
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const TINTS = ['#ffffff', '#ffffff', '#ffffff', '#9fd8ff', '#ffb3d9', '#c9b6ff', '#ffe2a8']
    let list = []
    let lastScroll = scrollY
    let surge = 0

    const resize = () => {
      canvas.width = innerWidth
      canvas.height = innerHeight
      list = Array.from({ length: Math.round((innerWidth * innerHeight) / 7000) }, () => ({
        x: Math.random() * innerWidth,
        y: Math.random() * innerHeight,
        r: Math.random() * 1.15 + 0.25,
        c: TINTS[(Math.random() * TINTS.length) | 0],
        p: Math.random() * 6.28,
        v: Math.random() * 0.012 + 0.003,
      }))
    }

    const draw = (t) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      surge += (scrollY - lastScroll - surge) * 0.15
      lastScroll = scrollY
      for (const s of list) {
        ctx.globalAlpha = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(s.p + t * s.v * 0.2))
        ctx.fillStyle = s.c
        // Nearer stars move further with the scroll and streak while it's moving.
        const y = (((s.y - t * s.v * 0.5 - scrollY * s.r * 0.22) % innerHeight) + innerHeight) % innerHeight
        ctx.fillRect(s.x, y, s.r, s.r + Math.min(Math.abs(surge) * s.r * 0.7, 70))
      }
      if (!STILL) requestAnimationFrame(draw)
    }

    resize()
    addEventListener('resize', resize)
    draw(0)
  }

  const cleanups = new Map()
  function init(root) {
    const stops = []
    root.querySelectorAll('.marquee[data-text]').forEach((el) => stops.push(marquee(el)))
    root.querySelectorAll('[role=tablist]').forEach((el) => stops.push(tabs(el.parentElement)))
    if (!STILL) {
      root.querySelectorAll('.grain-fan[data-cards]').forEach((el) => stops.push(grainFan(el)))
      root.querySelectorAll('.grain-img').forEach((el) => { if (!el.closest('.index-card')) stops.push(grainImage(el)) })
    }
    root.querySelectorAll('.index').forEach((el) => stops.push(indexMenu(el)))
    reveal(root)
    cleanups.set(root, stops)
  }
  function teardown(root) {
    for (const stop of cleanups.get(root) || []) stop()
    cleanups.delete(root)
  }

  if (!STILL) makeFrames()
  stars()
  document.querySelectorAll('.shopify-section').forEach(init)
  document.documentElement.dataset.ready = ''

  // Theme editor: a section that is added, changed or removed gets its effects rebuilt or stopped.
  document.addEventListener('shopify:section:load', (e) => init(e.target))
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target))
})()
